import React, { useState, useEffect } from 'react'
import {
  Box, Button, Chip, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, CircularProgress
} from '@material-ui/core'
import axios from 'axios'

const round2 = v => Math.round(v * 100) / 100

const MaterialStage = ({ assemblies, url, projectId, onRefresh }) => {
  const [materials, setMaterials] = useState([])
  const [selectedAssembly, setSelectedAssembly] = useState(null)
  const [loading, setLoading] = useState(false)
  const [allocQtys, setAllocQtys] = useState({})

  const API = `${url}/api/bid-estimation`

  // Load available materials
  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true)
        const res = await axios.get(`${API}/projects/${projectId}/assembly/available-materials`)
        setMaterials(Array.isArray(res.data) ? res.data : [])
      } catch (err) {
        console.error('Fetch materials failed:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchMaterials()
  }, [API, projectId, assemblies])

  const handleAssociate = async (material) => {
    if (!selectedAssembly) return
    const qty = allocQtys[`${material.vendorQuoteId}_${material.vendorLineIndex}`] || material.remainingQty
    try {
      await axios.post(`${API}/assembly/${selectedAssembly._id}/materials`, {
        vendorQuoteId: material.vendorQuoteId,
        vendorLineIndex: material.vendorLineIndex,
        allocatedQty: qty,
        description: material.description,
        vendor: material.vendor,
        vendorSku: material.vendorSku,
        unitPrice: material.unitPrice,
        unit: material.unit,
        catalogItemId: material.catalogItemId
      })
      onRefresh()
      // Refresh materials to update allocated counts
      const res = await axios.get(`${API}/projects/${projectId}/assembly/available-materials`)
      setMaterials(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('Associate material failed:', err)
    }
  }

  const handleRemoveMaterial = async (assemblyId, idx) => {
    try {
      await axios.delete(`${API}/assembly/${assemblyId}/materials/${idx}`)
      onRefresh()
      const res = await axios.get(`${API}/projects/${projectId}/assembly/available-materials`)
      setMaterials(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('Remove material failed:', err)
    }
  }

  // Only show assemblies that have services assigned
  const assignedAssemblies = assemblies.filter(a =>
    a.assemblyStatus !== 'uncategorized' && a.assemblyStatus !== 'categorized' || a.materials?.length > 0
  )

  return (
    <Grid container spacing={2}>
      {/* Left Panel: Bid Lines */}
      <Grid item xs={6}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          Bid Lines ({assignedAssemblies.length})
        </Typography>
        <TableContainer style={{ maxHeight: 600, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>#</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Qty</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Materials</TableCell>
                <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Mat Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedAssemblies.map(asm => {
                const bi = asm.bidItem || {}
                const isSelected = selectedAssembly?._id === asm._id
                return (
                  <React.Fragment key={asm._id}>
                    <TableRow
                      hover
                      selected={isSelected}
                      onClick={() => setSelectedAssembly(asm)}
                      style={{ cursor: 'pointer', backgroundColor: isSelected ? '#e3f2fd' : undefined }}
                    >
                      <TableCell style={{ fontSize: 11 }}>{bi.itemNo || '-'}</TableCell>
                      <TableCell style={{ fontSize: 11, maxWidth: 250 }}>
                        {bi.descriptionRaw || bi.descriptionNormalized || '-'}
                        {asm.serviceName && (
                          <Chip label={asm.serviceName} size="small" style={{ marginLeft: 4, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell align="right" style={{ fontSize: 11 }}>{bi.quantity ?? '-'}</TableCell>
                      <TableCell style={{ fontSize: 11 }}>
                        {(asm.materials || []).length > 0 ? `${asm.materials.length} linked` : '-'}
                      </TableCell>
                      <TableCell align="right" style={{ fontSize: 11, fontWeight: 600 }}>
                        {asm.costBreakdown?.materialCost ? `$${round2(asm.costBreakdown.materialCost).toLocaleString()}` : '-'}
                      </TableCell>
                    </TableRow>
                    {/* Show linked materials nested */}
                    {isSelected && (asm.materials || []).map((mat, idx) => (
                      <TableRow key={idx} style={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell />
                        <TableCell colSpan={2} style={{ fontSize: 10, paddingLeft: 24 }}>
                          {mat.description} ({mat.vendor})
                        </TableCell>
                        <TableCell align="right" style={{ fontSize: 10 }}>
                          {mat.allocatedQty} {mat.unit} @ ${mat.unitPrice}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleRemoveMaterial(asm._id, idx) }}
                            style={{ fontSize: 10, color: '#ef4444', textTransform: 'none', minWidth: 0, padding: '0 4px' }}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Right Panel: Available Materials */}
      <Grid item xs={6}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          Available Materials ({materials.length})
          {selectedAssembly && (
            <Chip
              label={`Adding to: ${selectedAssembly.bidItem?.descriptionRaw?.substring(0, 40) || 'selected'}`}
              size="small"
              color="primary"
              style={{ marginLeft: 8, fontSize: 10 }}
            />
          )}
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress size={24} /></Box>
        ) : (
          <TableContainer style={{ maxHeight: 600, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Description</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Vendor</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Price</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Total</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Alloc</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }} align="right">Remain</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }}>Qty</TableCell>
                  <TableCell style={{ fontWeight: 700, fontSize: 11 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map((mat, i) => {
                  const key = `${mat.vendorQuoteId}_${mat.vendorLineIndex}`
                  return (
                    <TableRow key={key} hover style={{ opacity: mat.remainingQty <= 0 ? 0.4 : 1 }}>
                      <TableCell style={{ fontSize: 11, maxWidth: 200 }}>
                        {mat.description || '-'}
                        {mat.vendorSku && <Typography variant="caption" display="block" color="textSecondary">{mat.vendorSku}</Typography>}
                      </TableCell>
                      <TableCell style={{ fontSize: 11 }}>{mat.vendor || '-'}</TableCell>
                      <TableCell align="right" style={{ fontSize: 11 }}>${mat.unitPrice}</TableCell>
                      <TableCell align="right" style={{ fontSize: 11 }}>{mat.totalQty}</TableCell>
                      <TableCell align="right" style={{ fontSize: 11 }}>{mat.allocatedQty}</TableCell>
                      <TableCell align="right" style={{ fontSize: 11, fontWeight: 600, color: mat.remainingQty > 0 ? '#10b981' : '#ef4444' }}>
                        {mat.remainingQty}
                      </TableCell>
                      <TableCell style={{ width: 80 }}>
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          value={allocQtys[key] ?? ''}
                          onChange={e => setAllocQtys(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                          placeholder={String(mat.remainingQty)}
                          inputProps={{ min: 0, max: mat.remainingQty, style: { fontSize: 11, padding: '4px 6px', width: 50 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={!selectedAssembly || mat.remainingQty <= 0}
                          onClick={() => handleAssociate(mat)}
                          style={{ textTransform: 'none', fontSize: 10, minWidth: 0, padding: '2px 8px' }}
                        >
                          Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {materials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" style={{ padding: 32, color: '#999' }}>
                      No vendor quote materials found for this project.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  )
}

export default MaterialStage
